import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { FaChartBar, FaFilter, FaDownload, FaGamepad, FaWallet, FaChartLine } from "react-icons/fa";
import { FiRefreshCw, FiDollarSign, FiTrendingUp } from "react-icons/fi";
import { BsCashStack, BsArrowUpRight, BsArrowDownRight } from "react-icons/bs";
import { Icon } from "@iconify/react";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { toast } from "react-toastify";
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  flexRender,
} from "@tanstack/react-table";

/* ─── Styles ─── */
const s = {
  root: { padding: "20px 24px 40px", background: "#f1f5f9", minHeight: "100vh", fontFamily: "'Inter', sans-serif" },
  headerCard: { background: "#fff", borderRadius: 16, padding: "20px 24px", display: "flex", flexWrap: "wrap", justifyContent: "space-between", alignItems: "center", border: "1px solid #e8edf2", boxShadow: "0 1px 4px rgba(0,0,0,0.04)", marginBottom: 20 },
  headerTitleWrap: { display: "flex", alignItems: "center", gap: 12 },
  headerIconWrap: { background: "#eff6ff", padding: 12, borderRadius: 12, color: "#3b82f6" },
  headerTitle: { margin: 0, fontWeight: 800, fontSize: "1.4rem", color: "#0f172a" },
  headerSub: { margin: "2px 0 0", color: "#64748b", fontSize: "0.85rem" },
  btnGroup: { display: "flex", gap: "12px", flexWrap: "wrap" },
  btnOutlined: { display: "flex", alignItems: "center", gap: 6, padding: "8px 18px", border: "1.5px solid #e2e8f0", background: "#fff", borderRadius: 9, fontSize: "0.85rem", fontWeight: 700, color: "#475569", cursor: "pointer", transition: "all 0.2s" },
  btnSave: { display: "flex", alignItems: "center", gap: 6, padding: "8px 20px", background: "linear-gradient(135deg,#16632b,#1da144)", border: "none", borderRadius: 9, fontSize: "0.85rem", fontWeight: 700, color: "#fff", cursor: "pointer", transition: "all 0.2s" },
  filterBox: { background: "#fff", borderRadius: 16, padding: "20px 24px", border: "1px solid #e8edf2", boxShadow: "0 1px 4px rgba(0,0,0,0.04)", marginBottom: 24, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 16 },
  label: { display: "block", fontSize: "0.75rem", fontWeight: 700, color: "#475569", marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.04em" },
  input: { width: "100%", padding: "9px 12px", border: "1.5px solid #e2e8f0", borderRadius: 9, fontSize: "0.87rem", color: "#1e293b", background: "#f8fafc", outline: "none", fontFamily: "inherit", boxSizing: "border-box" },
  summaryGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: "16px", marginBottom: "24px" },
  sumCard: { padding: "20px", borderRadius: 16, color: "#fff", display: "flex", flexDirection: "column", gap: 10, boxShadow: "0 4px 12px rgba(0,0,0,0.05)" },
  sumLabel: { fontSize: "0.85rem", fontWeight: 700, opacity: 0.9, textTransform: "uppercase", letterSpacing: "0.05em" },
  sumValue: { fontSize: "1.8rem", fontWeight: 900, lineHeight: 1 },
  sumIcon: { background: "rgba(255,255,255,0.2)", padding: 8, borderRadius: 10, display: "inline-flex" },
  tabRow: { display: "flex", gap: "8px", overflowX: "auto", borderBottom: "2px solid #e2e8f0", paddingBottom: "10px", marginBottom: "20px" },
  tabBtn: { padding: "10px 20px", background: "none", border: "none", borderRadius: 8, fontSize: "0.9rem", fontWeight: 700, cursor: "pointer", color: "#64748b", transition: "all 0.2s", whiteSpace: "nowrap" },
  tabActive: { background: "#0f172a", color: "#fff", boxShadow: "0 2px 6px rgba(15,23,42,0.15)" },
  card: { background: "#fff", borderRadius: 16, border: "1px solid #e8edf2", boxShadow: "0 1px 4px rgba(0,0,0,0.04)", overflow: "hidden" },
  toolbar: { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 16px", borderBottom: "1px solid #f1f5f9", flexWrap: "wrap", gap: 10 },
  searchWrap: { position: "relative", display: "flex", alignItems: "center" },
  searchIcon: { position: "absolute", left: 10, fontSize: 15, color: "#94a3b8", pointerEvents: "none" },
  searchInput: { padding: "7px 30px 7px 32px", border: "1.5px solid #e2e8f0", borderRadius: 9, fontSize: "0.82rem", color: "#334155", background: "#f8fafc", outline: "none", width: 240, fontFamily: "inherit" },
  tableWrap: { overflowX: "auto" },
  table: { width: "100%", borderCollapse: "collapse", fontSize: "0.82rem" },
  th: { padding: "12px 16px", background: "#f8fafc", color: "#64748b", fontWeight: 700, fontSize: "0.71rem", textTransform: "uppercase", letterSpacing: "0.05em", borderBottom: "1.5px solid #f1f5f9", whiteSpace: "nowrap", textAlign: "left" },
  td: { padding: "12px 16px", borderBottom: "1px solid #f8fafc", color: "#334155", verticalAlign: "middle" },
  pgInfo: { fontSize: "0.78rem", color: "#64748b" },
  pgBtns: { display: "flex", gap: 4 },
  pgBtn: { minWidth: 30, height: 30, border: "1px solid #e2e8f0", background: "#fff", borderRadius: 7, cursor: "pointer", fontSize: "0.78rem", color: "#475569", display: "flex", alignItems: "center", justifyContent: "center" },
  pgBtnActive: { background: "linear-gradient(135deg,#16632b,#1da144)", color: "#fff", border: "none", fontWeight: 700 },
  miniSummaryWrap: { display: "flex", gap: "16px", padding: "16px", background: "#f8fafc", borderBottom: "1px solid #f1f5f9" },
  miniVal: { fontSize: "1.1rem", fontWeight: 800, color: "#0f172a" },
  miniTitle: { fontSize: "0.7rem", color: "#64748b", fontWeight: 700, textTransform: "uppercase" },
};

/* ─── Helpers ─── */
const formatCurrency = (amt) => {
  if (!amt) return "₹0";
  return new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(amt);
};

const formatNumber = (num) => {
  if (!num) return "0";
  return new Intl.NumberFormat("en-IN").format(num);
};

const SortIcon = ({ col }) => {
  if (!col.getCanSort()) return null;
  const dir = col.getIsSorted();
  return <span style={{ marginLeft: 4, opacity: dir ? 1 : 0.35, fontSize: 11 }}>{dir === "asc" ? "▲" : dir === "desc" ? "▼" : "⇅"}</span>;
};

const StatusBadge = ({ status }) => {
  const map = {
    live: { bg: "#dcfce7", color: "#16a34a" },
    active: { bg: "#dcfce7", color: "#16a34a" },
    settled: { bg: "#e0f2fe", color: "#0284c7" },
    completed: { bg: "#e0f2fe", color: "#0284c7" },
    closed: { bg: "#fee2e2", color: "#dc2626" },
    deleted: { bg: "#fee2e2", color: "#dc2626" },
    upcoming: { bg: "#fef3c7", color: "#d97706" }
  };
  const m = map[status?.toLowerCase()] || { bg: "#f1f5f9", color: "#64748b" };
  return (
    <span style={{ background: m.bg, color: m.color, padding: "3px 10px", borderRadius: 100, fontSize: "0.68rem", fontWeight: 800, textTransform: "uppercase", border: `1px solid ${m.bg}` }}>
      {status}
    </span>
  );
};


/* ─── Reusable Data Grid ─── */
const DataTable = ({ columns, data, summary }) => {
  const [globalFilter, setGlobalFilter] = useState("");
  const [sorting, setSorting] = useState([]);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 15 });

  const table = useReactTable({
    data, columns, state: { globalFilter, sorting, pagination },
    onGlobalFilterChange: setGlobalFilter, onSortingChange: setSorting, onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(), getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(), getPaginationRowModel: getPaginationRowModel(),
  });

  const { pageIndex, pageSize } = table.getState().pagination;
  const pageCount = table.getPageCount();
  const rawCount = table.getFilteredRowModel().rows.length;

  return (
    <div style={s.card}>
      {summary && (
        <div style={s.miniSummaryWrap}>
          {summary.map((item, i) => (
             <div key={i}>
                <div style={s.miniTitle}>{item.label}</div>
                <div style={s.miniVal}>{item.val}</div>
             </div>
          ))}
        </div>
      )}
      <div style={s.toolbar}>
        <div style={s.searchWrap}>
          <Icon icon="ion:search-outline" style={s.searchIcon} />
          <input
            style={s.searchInput}
            placeholder="Search within subset..."
            value={globalFilter}
            onChange={(e) => setGlobalFilter(e.target.value)}
          />
        </div>
        <select style={{...s.input, width: "auto", minWidth: 80}} value={pageSize} onChange={e=>table.setPageSize(Number(e.target.value))}>
          {[15,30,50,100].map(n=><option key={n} value={n}>Show {n}</option>)}
        </select>
      </div>

      <div style={s.tableWrap}>
        <table style={s.table}>
          <thead>
            {table.getHeaderGroups().map(hg => (
              <tr key={hg.id}>
                {hg.headers.map(header => (
                  <th key={header.id} style={{ ...s.th, cursor: header.column.getCanSort() ? "pointer" : "default" }} onClick={header.column.getToggleSortingHandler()}>
                    {flexRender(header.column.columnDef.header, header.getContext())}
                    <SortIcon col={header.column} />
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {rawCount > 0 ? table.getRowModel().rows.map((row, idx) => (
              <tr key={row.id} style={{ background: idx % 2 === 0 ? "#fff" : "#fafbfc" }}>
                {row.getVisibleCells().map(cell => (
                  <td key={cell.id} style={s.td}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            )) : (
              <tr><td colSpan={columns.length} style={{ padding: "40px", textAlign: "center", color: "#94a3b8" }}>No data points found.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 16px", borderTop: "1px solid #f1f5f9" }}>
        <div style={s.pgInfo}>Showing {rawCount > 0 ? pageIndex*pageSize+1 : 0}–{Math.min((pageIndex+1)*pageSize, rawCount)} of {rawCount}</div>
        <div style={s.pgBtns}>
           <button style={s.pgBtn} onClick={() => table.previousPage()} disabled={!table.getCanPreviousPage()}>‹</button>
           <span style={{ fontSize: "0.78rem", fontWeight: 600, padding: "0 8px", alignSelf: "center", color: "#475569" }}>
             Page {table.getState().pagination.pageIndex + 1} of {table.getPageCount() || 1}
           </span>
           <button style={s.pgBtn} onClick={() => table.nextPage()} disabled={!table.getCanNextPage()}>›</button>
        </div>
      </div>
    </div>
  );
};


/* ─── Column Definitions ─── */
const matchCols = [
  { accessorKey: "match_id", header: "ID", cell: v => <span style={{ fontFamily: "monospace", color: "#64748b" }}>#{v.getValue()}</span> },
  { accessorKey: "league_id", header: "League", cell: v => <span style={{ fontWeight: 600 }}>{v.getValue()}</span> },
  { accessorKey: "status", header: "Status", cell: v => <StatusBadge status={v.getValue()}/> },
  { id: "collection", header: "Collection", accessorFn: row => row.financials?.total_collection, cell: v => <span style={{ fontWeight: 800, color: "#1e293b" }}>{formatCurrency(v.getValue())}</span> },
  { id: "payout", header: "Payout", accessorFn: row => row.financials?.total_paid_to_users, cell: v => <span style={{ fontWeight: 700, color: "#dc2626" }}>{formatCurrency(v.getValue())}</span> },
  { id: "profit", header: "Profit", accessorFn: row => row.financials?.admin_profit, cell: v => <span style={{ fontWeight: 800, color: "#16a34a" }}>{formatCurrency(v.getValue())}</span> },
  { id: "side", header: "Side A/B", enableSorting: false, cell: ({row}) => <span style={{ fontSize: "0.75rem", fontFamily: "monospace" }}>{formatCurrency(row.original.side_breakdown?.side_a_amount)} / {formatCurrency(row.original.side_breakdown?.side_b_amount)}</span>}
];

const questionCols = [
  { accessorKey: "question_id", header: "ID", cell: v => <span style={{ fontFamily: "monospace", color: "#64748b" }}>#{v.getValue()}</span> },
  { accessorKey: "match_id", header: "Match ID", cell: v => <span style={{ color: "#475569" }}>{v.getValue()}</span> },
  { accessorKey: "status", header: "Status", cell: v => <StatusBadge status={v.getValue()}/> },
  { accessorKey: "correct_option", header: "Result", cell: v => <span style={{ fontWeight: 700, color: "#1e293b" }}>{v.getValue() || "—"}</span> },
  { id: "entries", header: "Entries", accessorFn: row => row.financials?.total_entries, cell: v => <span style={{ fontWeight: 700 }}>{formatNumber(v.getValue())}</span> },
  { id: "collection", header: "Collection", accessorFn: row => row.financials?.total_collection, cell: v => <span style={{ fontWeight: 800, color: "#1e293b" }}>{formatCurrency(v.getValue())}</span> },
  { id: "payout", header: "Payout", accessorFn: row => row.financials?.total_payout, cell: v => <span style={{ fontWeight: 700, color: "#dc2626" }}>{formatCurrency(v.getValue())}</span> },
  { id: "wlr", header: "W/L/R", enableSorting: false, cell: ({row}) => (
      <div style={{ fontSize: "0.7rem", fontWeight: 700 }}>
        <span style={{ color: "#16a34a", marginRight: 6 }}>W:{row.original.counts?.winner_count || 0}</span>
        <span style={{ color: "#dc2626", marginRight: 6 }}>L:{row.original.counts?.loser_count || 0}</span>
        <span style={{ color: "#d97706" }}>R:{row.original.counts?.refund_count || 0}</span>
      </div>
  )}
];

const selectionCols = [
  { accessorKey: "game_id", header: "ID", cell: v => <span style={{ fontFamily: "monospace", color: "#64748b" }}>#{v.getValue()}</span> },
  { accessorKey: "match_id", header: "Match ID", cell: v => <span style={{ color: "#475569" }}>{v.getValue()}</span> },
  { accessorKey: "title", header: "Title", cell: v => <div style={{ maxWidth: 200, WebkitLineClamp: 1, overflow: "hidden", textOverflow: "ellipsis", display: "-webkit-box", WebkitBoxOrient: "vertical", fontWeight: 600 }}>{v.getValue() || "Untitled"}</div> },
  { accessorKey: "status", header: "Status", cell: v => <StatusBadge status={v.getValue()}/> },
  { id: "entries", header: "Entries", accessorFn: row => row.financials?.total_entries, cell: v => <span style={{ fontWeight: 700 }}>{formatNumber(v.getValue())}</span> },
  { id: "collection", header: "Collection", accessorFn: row => row.financials?.total_collection, cell: v => <span style={{ fontWeight: 800, color: "#1e293b" }}>{formatCurrency(v.getValue())}</span> },
  { id: "cut", header: "Admin Cut", accessorFn: row => row.financials?.admin_cut_amount, cell: v => <span style={{ fontWeight: 800, color: "#16a34a" }}>{formatCurrency(v.getValue())}</span> },
  { id: "wl", header: "W/L", enableSorting: false, cell: ({row}) => (
      <div style={{ fontSize: "0.7rem", fontWeight: 700 }}>
        <span style={{ color: "#16a34a", marginRight: 6 }}>W:{row.original.counts?.winner_count || 0}</span>
        <span style={{ color: "#dc2626" }}>L:{row.original.counts?.loser_count || 0}</span>
      </div>
  )}
];

const pointsCols = [
  { accessorKey: "game_id", header: "ID", cell: v => <span style={{ fontFamily: "monospace", color: "#64748b" }}>#{v.getValue()}</span> },
  { accessorKey: "match_id", header: "Match ID", cell: v => <span style={{ color: "#475569" }}>{v.getValue()}</span> },
  { accessorKey: "status", header: "Status", cell: v => <StatusBadge status={v.getValue()}/> },
  { accessorKey: "win_answer", header: "Win Cond", cell: v => <span style={{ fontWeight: 700, color: "#3b82f6" }}>{v.getValue() || "—"}</span> },
  { id: "entries", header: "Entries", accessorFn: row => row.financials?.total_entries, cell: v => <span style={{ fontWeight: 700 }}>{formatNumber(v.getValue())}</span> },
  { id: "collection", header: "Collection", accessorFn: row => row.financials?.total_collection, cell: v => <span style={{ fontWeight: 800, color: "#1e293b" }}>{formatCurrency(v.getValue())}</span> },
  { id: "payout", header: "Payout", accessorFn: row => row.financials?.total_payout, cell: v => <span style={{ fontWeight: 700, color: "#dc2626" }}>{formatCurrency(v.getValue())}</span> },
  { id: "profit", header: "Profit", accessorFn: row => row.financials?.admin_profit, cell: v => <span style={{ fontWeight: 800, color: "#16a34a" }}>{formatCurrency(v.getValue())}</span> }
];


/* ─── Main Layer ─── */
export const AllReportLayerList = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [activeTab, setActiveTab] = useState("MATCH");

  const [filters, setFilters] = useState({
    game_type: "all", status: "all",
    league_id: "", match_id: "",
    date_from: "", date_to: ""
  });

  const fetchReports = async () => {
    const tkn = localStorage.getItem("token") || sessionStorage.getItem("token") || "";
    if (!tkn) return toast.error("Authentication required");

    setLoading(true);
    try {
      const res = await axios.get("https://opiniongames.net/api/super-admin/master-reports", {
        headers: { Authorization: `Bearer ${tkn}` },
        params: { ...filters, section: "detailed" }
      });
      if (res.data?.status) setData(res.data);
      else toast.error("Failed to fetch reports");
    } catch {
      toast.error("Network or server error encountered");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReports();
    // eslint-disable-next-line
  }, []);

  const exportExcel = () => {
    if (!data) return;
    const rows = [];
    rows.push({ Category: "GRAND SUMMARY", Metric: "", Value: "" });
    rows.push({ Category: "", Metric: "Total Games", Value: data.grand_summary?.total_games_all_types });
    rows.push({ Category: "", Metric: "Total Collection", Value: data.grand_summary?.total_collection_all_games });
    rows.push({ Category: "", Metric: "Total Payout", Value: data.grand_summary?.total_payout_all_games });
    rows.push({ Category: "", Metric: "Admin Profit", Value: data.grand_summary?.total_admin_profit_all_games });
    rows.push({ Category: "", Metric: "Total Entries", Value: data.grand_summary?.total_entries_all_games });
    rows.push({ Category: "", Metric: "Total Winners", Value: data.grand_summary?.total_winners_all_games });
    rows.push({ Category: "", Metric: "Total Losers", Value: data.grand_summary?.total_losers_all_games });
    rows.push({ Category: "", Metric: "Total Refunded", Value: data.grand_summary?.total_refunded_all_games });
    rows.push({ Category: "", Metric: "", Value: "" });

    // Optional dynamic detail injection
    if (data.games_breakdown) {
       Object.keys(data.games_breakdown).forEach(key => {
         const cat = data.games_breakdown[key];
         if (cat?.summary) {
            rows.push({ Category: `[${cat.game_name}]`, Metric: "Summary Collection", Value: cat.summary.total_collection });
            rows.push({ Category: "", Metric: "Admin Profit", Value: cat.summary.total_admin_profit || cat.summary.total_admin_earning || 0 });
         }
       });
    }

    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Master Reports");
    const buffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const blob = new Blob([buffer], { type: "application/octet-stream" });
    saveAs(blob, `opinion-analytics-${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  return (
    <div style={s.root}>
      {/* Top Header Card */}
      <div style={s.headerCard}>
        <div style={s.headerTitleWrap}>
          <div style={s.headerIconWrap}><FaChartBar size={24} /></div>
          <div>
            <h4 style={s.headerTitle}>Master Analytics</h4>
            <p style={s.headerSub}>Live cross-game ledger and global financial reports</p>
          </div>
        </div>
        <div style={s.btnGroup}>
          <button style={{...s.btnOutlined, background: showFilters ? "#f8fafc" : "#fff"}} onClick={() => setShowFilters(!showFilters)}>
            <FaFilter size={14} /> Filters
          </button>
          <button style={s.btnOutlined} onClick={exportExcel} disabled={!data}>
            <FaDownload size={14} style={{ color: "#3b82f6" }} /> Export CSV
          </button>
          <button style={s.btnSave} onClick={fetchReports} disabled={loading}>
            {loading ? <span className="spinner-border spinner-border-sm" /> : <FiRefreshCw size={14} />} 
            {loading ? "Processing..." : "Refresh Report"}
          </button>
        </div>
      </div>

      {/* Embedded Deep Filter Panel */}
      {showFilters && (
        <div style={s.filterBox}>
          <div>
             <label style={s.label}>Game Focus</label>
             <select style={s.input} value={filters.game_type} onChange={e=>setFilters({...filters, game_type: e.target.value})}>
               <option value="all">All Modes</option>
               <option value="MATCH">A-Match</option>
               <option value="QUESTION">A-Question</option>
               <option value="SELECTION">C-Selection</option>
               <option value="POINTS">B-Points</option>
             </select>
          </div>
          <div>
             <label style={s.label}>Lifecycle Status</label>
             <select style={s.input} value={filters.status} onChange={e=>setFilters({...filters, status: e.target.value})}>
               <option value="all">Any Status</option>
               <option value="live">Live Only</option>
               <option value="settled">Settled / Closed</option>
               <option value="upcoming">Upcoming</option>
             </select>
          </div>
          <div>
             <label style={s.label}>Tracking ID</label>
             <input style={s.input} placeholder="League / Match / ID" value={filters.league_id} onChange={e=>setFilters({...filters, league_id: e.target.value})} />
          </div>
          <div>
             <label style={s.label}>From Date</label>
             <input type="date" style={s.input} value={filters.date_from} onChange={e=>setFilters({...filters, date_from: e.target.value})} />
          </div>
          <div>
             <label style={s.label}>To Date</label>
             <input type="date" style={s.input} value={filters.date_to} onChange={e=>setFilters({...filters, date_to: e.target.value})} />
          </div>
        </div>
      )}

      {/* Global Grand Summary Metrics */}
      {data?.grand_summary && (
        <div style={s.summaryGrid}>
          <div style={{ ...s.sumCard, background: "linear-gradient(135deg, #4b6cb7 0%, #182848 100%)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={s.sumLabel}>Total Collection</span>
              <span style={s.sumIcon}><BsCashStack size={20} /></span>
            </div>
            <div style={s.sumValue}>{formatCurrency(data.grand_summary.total_collection_all_games)}</div>
            <div style={{ fontSize: "0.8rem", opacity: 0.8 }}><FaGamepad /> {formatNumber(data.grand_summary.total_games_all_types)} Active Modes</div>
          </div>

          <div style={{ ...s.sumCard, background: "linear-gradient(135deg, #11998e 0%, #38ef7d 100%)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={s.sumLabel}>Admin Profit</span>
              <span style={s.sumIcon}><FiTrendingUp size={20} /></span>
            </div>
            <div style={s.sumValue}>{formatCurrency(data.grand_summary.total_admin_profit_all_games)}</div>
            <div style={{ fontSize: "0.8rem", opacity: 0.8 }}><BsArrowUpRight /> Revenue Stream Generated</div>
          </div>

          <div style={{ ...s.sumCard, background: "linear-gradient(135deg, #cb2d3e 0%, #ef473a 100%)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={s.sumLabel}>Total Payout</span>
              <span style={s.sumIcon}><FiDollarSign size={20} /></span>
            </div>
            <div style={s.sumValue}>{formatCurrency(data.grand_summary.total_payout_all_games)}</div>
            <div style={{ fontSize: "0.8rem", opacity: 0.8 }}><BsArrowDownRight /> Returned to users</div>
          </div>
        </div>
      )}

      {/* Game Type Navigation Layer */}
      {data?.games_breakdown && (
        <>
          <div style={s.tabRow}>
            {["A_MATCH_GAME", "A_QUESTION_GAME", "B_POINTS_GAME", "C_SELECTION_GAME"].map((k) => {
               if (!data.games_breakdown[k]) return null;
               const g = data.games_breakdown[k];
               const modeId = k.replace(/_GAME|A_|B_|C_/g, "");
               return (
                  <button 
                    key={k} 
                    style={{...s.tabBtn, ...(activeTab === modeId ? s.tabActive : {})}} 
                    onClick={() => setActiveTab(modeId)}
                  >
                     {g.game_name} ({modeId})
                  </button>
               );
            })}
          </div>

          {/* Conditional Grid Routing */}
          {activeTab === "MATCH" && data.games_breakdown?.A_MATCH_GAME && (
            <DataTable 
              columns={matchCols} 
              data={data.games_breakdown.A_MATCH_GAME.matches || []} 
              summary={[
                {label: "Matches", val: formatNumber(data.games_breakdown.A_MATCH_GAME.summary?.total_matches)},
                {label: "Bets", val: formatNumber(data.games_breakdown.A_MATCH_GAME.summary?.total_bets)},
                {label: "Collection", val: formatCurrency(data.games_breakdown.A_MATCH_GAME.summary?.total_collection)},
                {label: "Profit", val: formatCurrency(data.games_breakdown.A_MATCH_GAME.summary?.total_admin_profit)},
              ]}
            />
          )}

          {activeTab === "QUESTION" && data.games_breakdown?.A_QUESTION_GAME && (
            <DataTable 
              columns={questionCols} 
              data={data.games_breakdown.A_QUESTION_GAME.questions || []} 
              summary={[
                {label: "Questions", val: formatNumber(data.games_breakdown.A_QUESTION_GAME.summary?.total_questions)},
                {label: "Entries", val: formatNumber(data.games_breakdown.A_QUESTION_GAME.summary?.total_entries)},
                {label: "Collection", val: formatCurrency(data.games_breakdown.A_QUESTION_GAME.summary?.total_collection)},
                {label: "Profit", val: formatCurrency(data.games_breakdown.A_QUESTION_GAME.summary?.total_admin_profit)},
              ]}
            />
          )}

          {activeTab === "SELECTION" && data.games_breakdown?.C_SELECTION_GAME && (
            <DataTable 
              columns={selectionCols} 
              data={data.games_breakdown.C_SELECTION_GAME.games || []} 
              summary={[
                {label: "Games", val: formatNumber(data.games_breakdown.C_SELECTION_GAME.summary?.total_games)},
                {label: "Entries", val: formatNumber(data.games_breakdown.C_SELECTION_GAME.summary?.total_entries)},
                {label: "Collection", val: formatCurrency(data.games_breakdown.C_SELECTION_GAME.summary?.total_collection)},
                {label: "Earning", val: formatCurrency(data.games_breakdown.C_SELECTION_GAME.summary?.total_admin_earning)},
              ]}
            />
          )}

          {activeTab === "POINTS" && data.games_breakdown?.B_POINTS_GAME && (
            <DataTable 
              columns={pointsCols} 
              data={data.games_breakdown.B_POINTS_GAME.games || []} 
              summary={[
                {label: "Games", val: formatNumber(data.games_breakdown.B_POINTS_GAME.summary?.total_games)},
                {label: "Entries", val: formatNumber(data.games_breakdown.B_POINTS_GAME.summary?.total_entries)},
                {label: "Collection", val: formatCurrency(data.games_breakdown.B_POINTS_GAME.summary?.total_collection)},
                {label: "Profit", val: formatCurrency(data.games_breakdown.B_POINTS_GAME.summary?.total_admin_profit)},
              ]}
            />
          )}
        </>
      )}

    </div>
  );
};

export default AllReportLayerList;